import { useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

/**
 * Handles the return from Emergent Google OAuth.
 *
 * Emergent redirects back to `/auth/callback#session_id=...`. We parse
 * it, exchange for our JWT via /api/auth/google/session, then route the
 * user to /onboarding (new / not-yet-onboarded) or /beit-midrash.
 *
 * Why the `ranRef` guard:
 *   React.StrictMode (and any future re-mount caused by a dependency
 *   change in a parent context) would otherwise run this effect twice,
 *   producing two concurrent calls to the OAuth exchange endpoint AND
 *   — worse — a spurious "ההתחברות נכשלה" toast right after the success
 *   toast, because the second run reads an already-cleared hash. The
 *   ref is module-instance scoped: each real mount of the page resets
 *   it; the StrictMode immediate-remount keeps it set, so the second
 *   fire bails out silently.
 *
 * Why we clear the hash before awaiting:
 *   Even with the ref guard, any other unexpected re-fire would now
 *   find an empty hash and exit through the silent `!sessionId` branch
 *   instead of toasting a false error.
 */
export default function AuthCallback() {
  const { loginWithSession } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const ranRef = useRef(false);

  useEffect(() => {
    // Hard guard against StrictMode double-fire / any spurious re-run.
    if (ranRef.current) return;
    ranRef.current = true;

    // Read session_id from hash first, then query string, then `code=`
    // as a final fallback for proxies that strip hashes.
    const hash = window.location.hash || "";
    const hashParams = new URLSearchParams(
      hash.startsWith("#") ? hash.slice(1) : hash
    );
    const qs = new URLSearchParams(location.search);
    const sessionId =
      hashParams.get("session_id") ||
      qs.get("session_id") ||
      hashParams.get("code") ||
      qs.get("code");

    if (!sessionId) {
      // Silent — this branch is reached when the user lands on
      // /auth/callback without a session_id (e.g. direct navigation
      // or a StrictMode re-fire after a sibling cleared the hash).
      // We intentionally do NOT toast or navigate here so we don't
      // override a successful redirect already in progress.
      return;
    }

    // Clear the hash *now*, before the async exchange, so any later
    // re-run of this effect sees an empty URL and exits silently above.
    try {
      window.history.replaceState({}, "", "/auth/callback");
    } catch {
      /* ignore — non-fatal */
    }

    // No `cancelled` flag / cleanup here on purpose. StrictMode runs
    // the cleanup synchronously between the two mounts, so a cancelled
    // flag would suppress the toast+navigate after the API resolves,
    // leaving the user stuck on the spinner. The `ranRef` already
    // guarantees the async work runs exactly once per real mount. If
    // the user truly navigates away before resolution, a stray
    // navigate() call is a benign no-op handled by react-router.
    (async () => {
      try {
        const res = await loginWithSession(sessionId);
        toast.success(
          res?.is_new_user
            ? t("auth.toast_register_success")
            : t("auth.toast_login_success")
        );
        // New Google signups → onboarding. Users with onboarding not
        // completed (legacy / pre-feature) → also onboarding. Everyone
        // else → Beit Midrash workspace.
        const needsOnboarding =
          res?.is_new_user || res?.user?.onboarding_completed === false;
        navigate(needsOnboarding ? "/onboarding" : "/beit-midrash", {
          replace: true,
        });
      } catch (e) {
        toast.error(e?.response?.data?.detail || t("auth.toast_google_signin_failed"));
        navigate("/auth", { replace: true });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      className="min-h-[60vh] flex items-center justify-center"
      data-testid="auth-callback"
    >
      <div className="text-center space-y-4">
        <div className="h-10 w-10 mx-auto rounded-full border-2 border-gold/30 border-t-gold animate-spin" />
        <p className="font-heading text-lg text-foreground">מתחבר לגוגל...</p>
      </div>
    </div>
  );
}
